import { type ModuleKey } from './constants';
import { parseTimeValue } from './time-value';
import { m } from '@/i18n';
import { errOf, ok, type Result } from './types';

/**
 * The **module kit**: the standardized interface by which an optional functionality
 * module ties into the core (work items + the append-only log + projections). The
 * core is the spine; a module *layers on* through this one contract, and a thin host
 * in each layer (server / read / app) iterates the *enabled* modules and invokes the
 * relevant facet. The core never names a specific module.
 *
 * This file is pure (no DB, no framework): a module's data shape and its validation
 * live here; the effectful hosts are the edges. That mirrors the contract spine's
 * gather-then-decide split (the kit decides, the host gathers/writes).
 *
 * Built now: `fields` (data), `validateField` (write), the facets that drive rollup
 * (`aggregate` / `visibility`) and ownership (`owner`). Derived *facts* (e.g. `late`)
 * and what they *mean* are not per-module: the reconciliation engine (`read/reconciliation.ts`)
 * computes facts from the authored fields + the graph and applies the designed-in
 * consequence (docs/RECONCILIATION.md). `ref` fields are live for authored actor
 * fields (docs/FIELDS_AND_TAGS.md §2: person/group, constrained via `refKinds`,
 * never minting access) and for schedule anchors. Architected-for, not built:
 * `collection` field types, custom module commands + events, a module owning its
 * own projection/table, and the per-group `variant` (display style) selection.
 */

// --- field facets (the data contract a module declares per field) -----------

/** Authored = someone writes it (intent); derived = the core computes it (reality). */
export type FieldSource = 'authored' | 'derived';
/** Who owns an authored field: drives authority + the out-of-role flag and the
 *  plan/actual split (DESIGN.md: plan fields requester-owned, actuals derived). */
export type FieldOwner = 'requester' | 'contributor' | 'either';
/** Stored data type. `timevalue` is a grain-carrying time (date/week/quarter/range,
 *  see `time-value.ts`), stored as its canonical string. `ref` points at an actor/item;
 *  `collection` is a list of sub-records (both architected-for, e.g. per-person allocation).
 *
 *  **Not the same as `AUTHORED_FIELD_TYPES` in definitions.ts** (deliberate divergence):
 *  this covers ALL stored types including internal/derived/architected-for ones; the
 *  definitions set covers only what a person can author in the UI today (`text | number |
 *  date | selection`). `selection` (user word) maps to `enum` (technical word) across
 *  the boundary. Do not try to unify them - they are at different abstraction levels. */
export type FieldType = 'number' | 'text' | 'date' | 'timevalue' | 'enum' | 'ref' | 'collection';
/** Semantic/format on top of the type: drives unit, input step, and rendering. */
export type FieldFormat = 'fte-fraction' | 'percentage' | 'currency' | 'story-points' | 'duration';
/** How many values a field holds (docs/FIELDS_AND_TAGS.md §3): `many` turns a
 *  selection into a multi-select and an actor ref into a set. Absent = `one`.
 *  Only `enum` and `ref` fields may be `many`; a `many` value stores `string[]`. */
export type FieldCardinality = 'one' | 'many';
/** How a field rolls up the decomposition (over `parent_item_id`). */
export type FieldAggregate = 'sum' | 'avg' | 'max' | 'min' | 'none';
/** Public fields roll up / are management-visible; private stays group-local
 *  (DESIGN.md §384: aggregation sees only public module fields). */
export type FieldVisibility = 'public' | 'private';

export interface FieldDef {
  key: string;
  label: string;
  type: FieldType;
  source: FieldSource;
  aggregate: FieldAggregate;
  visibility: FieldVisibility;
  owner?: FieldOwner; // authored-only
  format?: FieldFormat; // semantic hint
  unit?: string;
  min?: number;
  max?: number;
  options?: readonly string[]; // enum
  /** `enum`/`ref` only; absent = `one`. A `many` field stores `string[]`. */
  cardinality?: FieldCardinality;
  /** `ref` only: the ref kinds this field may point at (e.g. `['person','group']`
   *  for an actor field). Checked purely by prefix; existence stays with the
   *  effectful host/picker. An actor-ref field NEVER mints access or authority
   *  (docs/FIELDS_AND_TAGS.md §2) - it records who, it does not seat or grant. */
  refKinds?: readonly string[];
  variant?: string; // constrained display variant, per-group overridable (architect-for)
  help?: string;
  /** `collection` only: validates and canonicalizes one row. The module owns its
   *  row shape (the kit stays module-agnostic); a collection field without a
   *  validator is not writable. Pure, like everything here. */
  validateRow?: (row: unknown) => Result<CollectionRow>;
  /** `collection` only: the property that identifies a row (e.g. `key`). When
   *  set, the kit rejects lists with duplicate ids - stable row identity is what
   *  bindings and cross-item references hang off. */
  rowKey?: string;
}

/** One record in a collection field's list (a module-defined shape, stored as JSON). */
export type CollectionRow = Record<string, unknown>;

/** A stored value: a scalar, a `string[]` for `many`-cardinality fields, or a
 *  list of rows for `collection` fields. */
export type FieldValue = number | string | null | string[] | CollectionRow[];

/** The scalar subset (every field type except `collection`). Surfaces that only
 *  handle scalars (proposal diffs, simple inputs) narrow through `scalarValue`. */
export type ScalarFieldValue = number | string | null;

export function scalarValue(v: FieldValue): ScalarFieldValue {
  return Array.isArray(v) ? null : v;
}

// --- the standardized interface ---------------------------------------------

export interface ModuleDefinition {
  /** a registry key, a context-sourced definition (`ctx:<ownerRef>` - the
   *  ontology carriers ride the same kit, docs/CONTEXT_RESOLUTION.md), or a
   *  flow's field namespace (`flow:<definitionId>`, docs/FLOW.md). */
  key: ModuleKey | `ctx:${string}` | `flow:${string}`;
  label: string;
  description: string;
  /** modules this one builds on (composition; e.g. a Gantt needs Time). */
  dependsOn?: readonly ModuleKey[];
  /** the module's namespaced slice of `work_items.module_data[key]`. */
  fields: readonly FieldDef[];
}

// --- generic helpers the hosts use (so the core never special-cases a module) -

export function fieldOf(def: ModuleDefinition, fieldKey: string): FieldDef | undefined {
  return def.fields.find((f) => f.key === fieldKey);
}

/** Authored fields a writer may set (derived fields are computed, never written). */
export function authoredFields(def: ModuleDefinition): FieldDef[] {
  return def.fields.filter((f) => f.source === 'authored');
}

/** Validate a `many`-cardinality write: each element through the single-value
 *  check, deduped in order; an empty result clears (like null). A bare scalar
 *  is accepted as a one-element list (client convenience). */
function validateMany(value: unknown, check: (raw: unknown) => Result<string>): Result<FieldValue> {
  const list = Array.isArray(value) ? value : [value];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of list) {
    const one = check(raw);
    if (!one.ok) return one;
    if (!seen.has(one.value)) {
      seen.add(one.value);
      out.push(one.value);
    }
  }
  return out.length > 0 ? ok(out) : ok(null);
}

/** Validate one authored field write against its facets. Pure (the `decide` analog). */
export function validateField(
  def: ModuleDefinition,
  fieldKey: string,
  value: unknown,
): Result<FieldValue> {
  const f = fieldOf(def, fieldKey);
  if (!f)
    return errOf(
      'invalid_input',
      m('itemErrors.field.unknown', { module: def.key, field: fieldKey }),
    );
  if (f.source !== 'authored')
    return errOf('invalid_input', m('itemErrors.field.derived', { label: f.label }));
  if (value === null || value === '') return ok(null); // clear
  if (Array.isArray(value) && value.length === 0) return ok(null); // empty list clears too
  const many = f.cardinality === 'many' && (f.type === 'enum' || f.type === 'ref');
  if (Array.isArray(value) && f.type !== 'collection' && !many) {
    return errOf('invalid_input', m('itemErrors.field.singleValue', { label: f.label }));
  }

  switch (f.type) {
    case 'number': {
      const n = typeof value === 'number' ? value : Number(value);
      if (!Number.isFinite(n))
        return errOf('invalid_input', m('itemErrors.field.notANumber', { label: f.label }));
      if (f.min !== undefined && n < f.min)
        return errOf(
          'invalid_input',
          m('itemErrors.field.belowMin', { label: f.label, min: String(f.min) }),
        );
      if (f.max !== undefined && n > f.max)
        return errOf(
          'invalid_input',
          m('itemErrors.field.aboveMax', { label: f.label, max: String(f.max) }),
        );
      return ok(n);
    }
    case 'enum': {
      const check = (raw: unknown): Result<string> => {
        const s = String(raw);
        if (!f.options?.includes(s)) {
          return errOf('invalid_input', m('itemErrors.field.notAllowed', { label: f.label }));
        }
        return ok(s);
      };
      if (many) return validateMany(value, check);
      return check(value);
    }
    case 'date': {
      const s = String(value);
      if (Number.isNaN(Date.parse(s)))
        return errOf('invalid_input', m('itemErrors.field.notADate', { label: f.label }));
      return ok(s);
    }
    case 'timevalue': {
      const parsed = parseTimeValue(String(value));
      return parsed.ok ? ok(parsed.value.canonical) : { ok: false, error: parsed.error };
    }
    case 'text':
      return ok(String(value));
    case 'ref': {
      // A ref stores the stable id of the thing it points at (an actor ref like
      // "person:<id>", or an anchor work item id). The pure layer checks shape
      // only - non-empty, and the kind prefix when `refKinds` constrains it;
      // existence and same-org belong to the effectful host (and the picker
      // constrains the choice).
      const check = (raw: unknown): Result<string> => {
        const s = String(raw).trim();
        if (s.length === 0)
          return errOf('invalid_input', m('itemErrors.field.emptyRef', { label: f.label }));
        if (f.refKinds) {
          const idx = s.indexOf(':');
          const kind = idx > 0 ? s.slice(0, idx) : '';
          if (!f.refKinds.includes(kind) || s.slice(idx + 1).length === 0) {
            return errOf(
              'invalid_input',
              m('itemErrors.field.wrongRefKind', {
                label: f.label,
                kinds: f.refKinds.join(' or '),
              }),
            );
          }
        }
        return ok(s);
      };
      if (many) return validateMany(value, check);
      return check(value);
    }
    case 'collection': {
      // A list of module-shaped rows; the module's validator owns the row shape
      // and canonicalizes each row. An empty list clears the field (like null).
      if (!f.validateRow) {
        return errOf(
          'invalid_input',
          m('itemErrors.field.collectionNotEditable', { label: f.label }),
        );
      }
      if (!Array.isArray(value))
        return errOf('invalid_input', m('itemErrors.field.notAList', { label: f.label }));
      if (value.length === 0) return ok(null);
      const rows: CollectionRow[] = [];
      const seen = new Set<string>();
      for (const raw of value) {
        const row = f.validateRow(raw);
        if (!row.ok) return row;
        if (f.rowKey) {
          const id = String(row.value[f.rowKey] ?? '');
          if (seen.has(id)) {
            return errOf(
              'invalid_input',
              m('itemErrors.field.duplicateRow', { label: f.label, rowKey: f.rowKey, id }),
            );
          }
          seen.add(id);
        }
        rows.push(row.value);
      }
      return ok(rows);
    }
  }
}

/** Value equality for change detection: scalars by identity, collections by
 *  canonical JSON (rows are already canonicalized by `validateRow`). */
export function sameFieldValue(a: FieldValue, b: FieldValue): boolean {
  if (Array.isArray(a) || Array.isArray(b)) {
    return JSON.stringify(a) === JSON.stringify(b);
  }
  return a === b;
}

/** Read a field's current value out of a work item's `module_data` blob. */
export function readField(
  moduleData: Record<string, unknown>,
  moduleKey: string,
  fieldKey: string,
): FieldValue {
  const grp = moduleData[moduleKey];
  if (grp && typeof grp === 'object') {
    const v = (grp as Record<string, unknown>)[fieldKey];
    if (typeof v === 'number' || typeof v === 'string') return v;
    if (Array.isArray(v)) return v as string[] | CollectionRow[];
  }
  return null;
}

/** Merge a validated value into a `module_data` blob immutably (`null` removes it). */
export function writeField(
  moduleData: Record<string, unknown>,
  moduleKey: string,
  fieldKey: string,
  value: FieldValue,
): Record<string, unknown> {
  const grp = { ...((moduleData[moduleKey] as Record<string, unknown> | undefined) ?? {}) };
  if (value === null) delete grp[fieldKey];
  else grp[fieldKey] = value;
  return { ...moduleData, [moduleKey]: grp };
}
